import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import DataTable from 'react-data-table-component';
import { api } from '../api';

function Orders() {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [noOfPages, setNoOfPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    api.getOrders(page, limit).then(res => {
      setOrders(res.orders);
      setPage(res.page);
      setLimit(res.limit);
      setTotal(res.total);
      setNoOfPages(res.noOfPages);
      setLoading(false);
    });
  }, [page, limit]);

  const filteredOrders = orders.filter(order => {
    // Status filter
    const statusMatch = statusFilter
      ? order.orderStatus && order.orderStatus.toLowerCase() === statusFilter.toLowerCase()
      : true;
    // Search filter
    const searchText = search.trim().toLowerCase();
    const searchMatch = searchText === "" || [
      order.id?.toString().toLowerCase(),
      order.customer?.toLowerCase(),
      order.seller?.toLowerCase(),
      order.product?.toLowerCase()
    ].some(field => field && field.includes(searchText));
    return statusMatch && searchMatch;
  });

  const columns = [
    { name: 'Order ID', selector: row => row.id, sortable: true },
    { name: 'Customer', selector: row => row.customer, sortable: true },
    { name: 'Seller', selector: row => row.seller, sortable: true },
    { name: 'Product', selector: row => row.product, sortable: false },
    { name: 'Order Status', selector: row => row.orderStatus, sortable: true, cell: row => {
      let colorClass = '';
      switch (row.orderStatus) {
        case 'DELIVERED': colorClass = 'badge bg-success'; break;
        case 'PENDING': colorClass = 'badge bg-warning text-dark'; break;
        case 'Cancelled': colorClass = 'badge bg-danger'; break;
        case 'Confirmed': colorClass = 'badge bg-primary'; break;
        case 'Out for Delivery': colorClass = 'badge bg-info text-dark'; break;
        default: colorClass = 'badge bg-secondary';
      }
      return <span className={colorClass}>{row.orderStatus}</span>;
    } },
    { name: 'Total Amount', selector: row => `$${row.total.toFixed(2)}`, sortable: true },
    { name: 'Delivery Type', selector: row => row.deliveryType, sortable: false },
    { name: 'Date & Time', selector: row => row.dateTime, sortable: true },
    { name: 'Payment Status', selector: row => row.paymentStatus, sortable: true, cell: row => {
      let colorClass = '';
      switch (row.paymentStatus) {
        case 'Paid': colorClass = 'badge bg-success'; break;
        case 'Pending': colorClass = 'badge bg-warning text-dark'; break;
        case 'Refunded': colorClass = 'badge bg-danger'; break;
        default: colorClass = 'badge bg-secondary';
      }
      return <span className={colorClass}>{row.paymentStatus}</span>;
    } },
    { name: 'Items', selector: row => row.items, sortable: false },
    {
      name: 'Action',
      cell: row => (
        <div className="btn-group mb-1">
          <button
            type="button"
            className="btn btn-outline-success"
            onClick={() => {
              setSelectedOrder(row);
              setTimeout(() => {
                const modal = window.bootstrap?.Modal
                  ? new window.bootstrap.Modal(document.getElementById('orderDetailModal'))
                  : null;
                if (modal) modal.show();
              }, 100);
            }}
          >View</button>
          <button type="button" className="btn btn-outline-success dropdown-toggle dropdown-toggle-split" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-display="static">
            <span className="sr-only">Action</span>
          </button>
          <div className="dropdown-menu">
            <a className="dropdown-item" href="#" onClick={e => {
              e.preventDefault();
              setSelectedOrder(row);
              setTimeout(() => {
                const modal = window.bootstrap?.Modal
                  ? new window.bootstrap.Modal(document.getElementById('orderDetailModal'))
                  : null;
                if (modal) modal.show();
              }, 100);
            }}>Details</a>
            <a className="dropdown-item" href="#" onClick={e => { e.preventDefault(); /* trigger notifications */ }}>Notify Customer</a>
            <a className="dropdown-item" href="#" onClick={e => { e.preventDefault(); /* trigger notifications */ }}>Notify Seller</a>
          </div>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  function markAs(statusKey, value) {
    if (!selectedOrder) return;
    setOrders(prev => prev.map(o => (o.id === selectedOrder.id ? { ...o, [statusKey]: value } : o)));
  }

  return (
    <>
      <Helmet>
        <title>Orders | Owena Admin Dashboard</title>
      </Helmet>
      <div className="ec-content-wrapper">
        <div className="content">
          <div className="breadcrumb-wrapper breadcrumb-contacts">
            <div>
              <h1>Order Listing</h1>
              <p className="breadcrumbs"><span><a href="#" onClick={e => e.preventDefault()}>Home</a></span>
                <span><i className="mdi mdi-chevron-right"></i></span>Orders</p>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">Order Status</label>
              <select id="orderStatusFilter" className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="out for delivery">Out for Delivery</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Search Orders</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by Order ID, Customer, Seller, Product..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="ec-vendor-list card card-default">
                <div className="card-body">
                  <DataTable
                    columns={columns}
                    data={filteredOrders}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={total}
                    paginationPerPage={limit}
                    paginationDefaultPage={page}
                    onChangePage={setPage}
                    onChangeRowsPerPage={setLimit}
                    highlightOnHover
                    pointerOnHover
                    responsive
                    noDataComponent={<div className="text-center">No orders found.</div>}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Order Detail Modal */}
          <div className="modal fade" id="orderDetailModal" tabIndex="-1" aria-labelledby="orderDetailModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header px-4">
                  <h5 className="modal-title" id="orderDetailModalLabel">Order Details</h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div className="modal-body px-4">
                  {selectedOrder ? (
                    <div>
                      <div className="row mb-3">
                        <div className="col-md-4"><strong>Order ID:</strong> {selectedOrder.id}</div>
                        <div className="col-md-4"><strong>Customer:</strong> {selectedOrder.customer}</div>
                        <div className="col-md-4"><strong>Date:</strong> {selectedOrder.dateTime}</div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4"><strong>Items:</strong> {selectedOrder.items}</div>
                        <div className="col-md-4"><strong>Total:</strong> ${selectedOrder.total.toFixed(2)}</div>
                        <div className="col-md-4"><strong>Payment:</strong> {selectedOrder.paymentStatus}</div>
                      </div>
                      <div className="row mb-4">
                        <div className="col-md-4"><strong>Fulfillment:</strong> {selectedOrder.orderStatus}</div>
                      </div>
                      <div className="d-flex gap-2">
                        <button className="btn btn-outline-primary" onClick={() => markAs('paymentStatus', 'Paid')}>Mark Paid</button>
                        <button className="btn btn-outline-secondary" onClick={() => markAs('orderStatus', 'Shipped')}>Mark Shipped</button>
                        <button className="btn btn-outline-success" onClick={() => markAs('orderStatus', 'Delivered')}>Mark Delivered</button>
                        <button className="btn btn-outline-danger" onClick={() => markAs('paymentStatus', 'Refunded')}>Refund</button>
                      </div>
                    </div>
                  ) : (
                    <p>No order selected.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Orders;